<template>
  <div v-if="currentUser && messages" class="mailbox-admin">
    <div class="page-header">
      <h1>Skrzynka odbiorcza</h1>
      <button @click="getMessages" class="btn-primary">Odśwież</button>
    </div>

    <div class="mailbox-layout">
      <div class="messages-section">
        <div class="section-card position-relative">
          <div class="section-card-header">
            <HintComponent page-key="admin_panel_messages_list" :absolute="true"/>
            <h2>Lista wiadomości</h2>
            <div class="search-bar">
              <input
                  type="text"
                  placeholder="Wyszukaj wiadomości..."
                  v-model="messageSearchInput"
                  @keyup.enter="searchMessages"
              >
              <button @click="searchMessages" class="btn-green">Szukaj</button>
              <button @click="resetSearch" class="btn-outline">Resetuj</button>
            </div>
          </div>

          <div class="messages-list">
            <button
                class="message-card"
                :class="{ selected: currentMessage && currentMessage.id === message.id }"
                v-for="message in messages"
                :key="message.id"
                @click="chooseMessage(message.id)"
                type="button"
            >
              <div class="message-card-meta">
                <span class="message-chip">Nowa wiadomość</span>
              </div>
              <h3 class="message-title">{{ splitTitle(message.title).subject }}</h3>
              <p class="message-desc">{{ splitContent(message.content).message }}</p>
              <div class="message-card-footer">
                <span class="message-author">{{ splitTitle(message.title).email }}</span>
              </div>
            </button>

            <div v-if="messages.length === 0" class="empty-state">
              <p>Brak wiadomości do wyświetlenia.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="preview-section">
        <div class="section-card">
          <div class="preview-header">
            <h2>Podgląd wiadomości</h2>
            <button
                v-if="currentMessage"
                @click="deleteMessage"
                class="btn-icon btn-danger"
                title="Usuń wiadomość"
            >
              <img src="@/assets/icons/trash.svg" alt="usuń">
            </button>
          </div>

          <div v-if="currentMessage" class="message-preview">
            <div class="preview-row">
              <span class="preview-label">Temat</span>
              <span class="preview-value">{{ splitContent(currentMessage.content).subject }}</span>
            </div>
            <div class="preview-row">
              <span class="preview-label">Adres email</span>
              <span class="preview-value">{{ splitTitle(currentMessage.title).email }}</span>
            </div>
            <div class="preview-row">
              <span class="preview-label">Numer telefonu</span>
              <span class="preview-value">{{ splitContent(currentMessage.content).phone }}</span>
            </div>
            <div class="preview-row preview-row-message">
              <span class="preview-label">Wiadomość</span>
              <p class="preview-message">{{ splitContent(currentMessage.content).message }}</p>
            </div>
          </div>

          <div v-else class="empty-state preview-empty">
            <p>Wybierz wiadomość z listy, aby zobaczyć szczegóły.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authHeader from '@/services/auth-header';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import HintComponent from "@/components/SpecialComponents/HintComponent.vue";

export default {
  name: 'MailBox',
  components: {HintComponent},
  setup() {
    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    };
  },
  data() {
    return {
      messages: null,
      allMessages: [],
      currentMessage: null,
      messageSearchInput: '',
    };
  },
  props: ['isVisible'],
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
    } else if (this.currentUser.role_id !== 1) {
      this.$router.push('/login');
    }
    this.getMessages();
  },
  computed: {
    currentUser() {
      return this.authStore.user;
    },
  },
  methods: {
    getMessages() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}MessageInBox/1`, authHeader())
          .then(res => {
            this.allMessages = Array.isArray(res.data) ? res.data : [];
            this.messages = [...this.allMessages];

            if (this.currentMessage) {
              const refreshedCurrent = this.messages.find(item => item.id === this.currentMessage.id);
              this.currentMessage = refreshedCurrent || null;
            }
          });
    },
    chooseMessage(id) {
      this.currentMessage = this.messages.find(item => item.id === id) || null;
    },
    deleteMessage() {
      if (!this.currentMessage) return;

      axios.delete(`${process.env.VUE_APP_API_BASE_URL}Message/${this.currentMessage.id}`, authHeader())
          .then(res => {
            if (String(res.status).charAt(0) === '2') {
              this.currentMessage = null;
              this.getMessages();
              this.appStore.setIsSuccess('Wiadomość została usunięta');
            }
          });
    },
    searchMessages() {
      const query = this.messageSearchInput.trim().toLowerCase();

      if (query.length > 2) {
        this.messages = this.allMessages.filter(message => {
          const title = (message.title || '').toLowerCase();
          const content = (message.content || '').toLowerCase();
          return title.includes(query) || content.includes(query);
        });

        if (this.currentMessage) {
          const stillVisible = this.messages.find(item => item.id === this.currentMessage.id);
          this.currentMessage = stillVisible || null;
        }
      } else {
        this.appStore.setIsAlert('Wpisz co najmniej 3 znaki');
      }
    },
    resetSearch() {
      this.messageSearchInput = '';
      this.messages = [...this.allMessages];
    },
    splitTitle(value) {
      const safe = String(value || '');
      const parts = safe.split('|');
      return {
        label: parts[0] || '',
        email: parts[1] || '',
      };
    },
    splitContent(value) {
      const safe = String(value || '');
      const parts = safe.split('|');
      return {
        subject: parts[0] || '',
        phone: parts[1] || '',
        message: parts[2] || '',
      };
    },
  },
};
</script>

<style scoped>
.mailbox-admin {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.mailbox-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.messages-section {
  flex: 1 1 0;
  min-width: 0;
}

.preview-section {
  width: 360px;
  flex-shrink: 0;
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.08);
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
}

.section-card h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.section-card-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.search-bar {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.search-bar input {
  flex: 1 1 160px;
  min-width: 0;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 1.5rem;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.search-bar input:focus {
  border-color: #10b981;
}

.btn-primary {
  background-color: #f97316;
  color: #fff;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: #ea6c0a;
}

.btn-green {
  background-color: #10b981;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-green:hover {
  background-color: #059669;
}

.btn-outline {
  background-color: transparent;
  color: #1f2937;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1.5rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 640px;
  overflow-y: auto;
  padding-right: 4px;
}

.message-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  text-align: left;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s, background-color 0.2s;
}

.message-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  border-color: #10b981;
}

.message-card.selected {
  border-color: #10b981;
  background-color: #f0fdf4;
}

.message-card-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.message-chip {
  background-color: #d1fae5;
  color: #065f46;
  font-size: 1.2rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.message-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.message-desc {
  font-size: 1.4rem;
  color: #6b7280;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.message-card-footer {
  margin-top: auto;
}

.message-author {
  font-size: 1.3rem;
  color: #9ca3af;
  word-break: break-word;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  transition: background-color 0.2s;
}

.btn-icon img {
  width: 20px;
  height: 20px;
}

.btn-icon:hover {
  background-color: #f3f4f6;
}

.btn-danger:hover {
  background-color: #fee2e2;
}

.message-preview {
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
  padding-top: 16px;
}

.preview-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-label {
  font-size: 1.3rem;
  color: #6b7280;
  font-weight: 600;
}

.preview-value {
  font-size: 1.5rem;
  color: #1f2937;
  word-break: break-word;
}

.preview-row-message {
  margin-top: 4px;
}

.preview-message {
  margin: 0;
  font-size: 1.5rem;
  color: #374151;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.empty-state {
  text-align: center;
  padding: 32px;
  color: #9ca3af;
  font-size: 1.5rem;
}

.preview-empty {
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
  padding-top: 24px;
}

@media (max-width: 980px) {
  .mailbox-layout {
    flex-direction: column;
  }

  .preview-section {
    width: 100%;
  }

  .messages-list {
    max-height: 420px;
  }
}

@media (max-width: 480px) {
  .mailbox-admin {
    padding: 16px 12px;
  }

  .page-header {
    margin-bottom: 20px;
  }

  .page-header h1 {
    font-size: 2.2rem;
  }

  .section-card {
    padding: 16px 12px;
    border-radius: 8px;
  }

  .section-card h2 {
    font-size: 1.8rem;
  }

  .search-bar {
    flex-direction: column;
  }

  .search-bar input,
  .btn-green,
  .btn-outline,
  .btn-primary {
    width: 100%;
  }
}
</style>